"use client";

import { useEffect, useState } from "react";
import {
  Timestamp,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { completionSchema, type Completion } from "@/lib/schemas";
import { todayMidnight } from "@/lib/utils/dates";
import { markReady } from "@/lib/realtime/registry";

type State = { completions: Completion[]; loading: boolean };

const cache = new Map<string, Completion[]>();
export const todaysHouseholdKey = (hid: string) => `todaysHH:${hid}`;

/**
 * Dzisiejsze completions WSZYSTKICH członków household. Analog
 * `useTodaysCompletions`, ale bez filtru `completedBy` — używany przez
 * widok rodzica w /chores, żeby status zadania uwzględniał wszystkie
 * dzieci (np. Iga zaliczyła "Spacer z psem" → karta oznaczona ✓).
 *
 * Realtime + module-level cache. Mountowany raz w `DataPrefetcher`.
 */
export function useTodaysHouseholdCompletions(
  householdId: string | undefined,
): State {
  const cached = householdId ? cache.get(householdId) : undefined;
  const [state, setState] = useState<State>({
    completions: cached ?? [],
    loading: cached === undefined,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const today = Timestamp.fromDate(todayMidnight());
    const q = query(
      refCol(db, paths.completions(householdId)),
      where("dueDate", "==", today),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const completions = snap.docs.flatMap((d) => {
          const parsed = completionSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(householdId, completions);
        markReady(todaysHouseholdKey(householdId));
        setState({ completions, loading: false });
      },
      () => setState({ completions: [], loading: false }),
    );
    return () => unsub();
  }, [householdId]);

  return state;
}
